import { Assets, type Texture } from "pixi.js";

type ImageAssetState = "queued" | "loading" | "loaded" | "unloading";

interface ImageAssetEntry {
    imageUrl: string;
    owners: Set<symbol>;
    state: ImageAssetState;
    texture: Texture | null;
    loadPromise: Promise<Texture> | null;
    resolveLoad: ((texture: Texture) => void) | null;
    rejectLoad: ((reason: unknown) => void) | null;
    cleanupPromise: Promise<void> | null;
    reloadPromise: Promise<Texture> | null;
}

export interface OwnedImageAsset {
    texturePromise: Promise<Texture>;
    release: () => void;
}

// Empirically tuned for large mosaics (~3000–4000 thumbnails). Higher concurrency
// gives diminishing returns; substantial further gains require reducing the number
// of individual image requests, e.g. by moving to a tiled image service.
const MAX_CONCURRENT_IMAGE_ASSET_LOADS = 32;

class ImageAssetManager {
    private readonly entries = new Map<string, ImageAssetEntry>();
    private readonly queuedEntries: ImageAssetEntry[] = [];
    private activeLoadCount = 0;

    acquire(imageUrl: string): OwnedImageAsset {
        const entry = this.getOrCreateEntry(imageUrl);
        const owner = Symbol(imageUrl);
        entry.owners.add(owner);

        const texturePromise = this.getTexturePromise(imageUrl, entry);
        let released = false;

        return {
            texturePromise,
            release: () => {
                if (released) return;
                released = true;
                this.release(imageUrl, entry, owner);
            },
        };
    }

    private getOrCreateEntry(imageUrl: string): ImageAssetEntry {
        const existing = this.entries.get(imageUrl);
        if (existing) return existing;

        const entry: ImageAssetEntry = {
            imageUrl,
            owners: new Set(),
            state: "queued",
            texture: null,
            loadPromise: null,
            resolveLoad: null,
            rejectLoad: null,
            cleanupPromise: null,
            reloadPromise: null,
        };
        this.entries.set(imageUrl, entry);
        return entry;
    }

    private getTexturePromise(
        imageUrl: string,
        entry: ImageAssetEntry,
    ): Promise<Texture> {
        if (entry.state === "loaded" && entry.texture !== null) {
            return Promise.resolve(entry.texture);
        }

        if (entry.state === "unloading") {
            return this.queueLoadAfterCleanup(imageUrl, entry);
        }

        return entry.loadPromise ?? this.queueLoad(entry);
    }

    private queueLoad(entry: ImageAssetEntry): Promise<Texture> {
        if (entry.loadPromise !== null) return entry.loadPromise;

        entry.state = "queued";
        entry.loadPromise = new Promise<Texture>((resolve, reject) => {
            entry.resolveLoad = resolve;
            entry.rejectLoad = reject;
        });
        this.queuedEntries.push(entry);
        this.drainQueue();
        return entry.loadPromise;
    }

    private queueLoadAfterCleanup(
        imageUrl: string,
        entry: ImageAssetEntry,
    ): Promise<Texture> {
        if (entry.reloadPromise !== null) return entry.reloadPromise;

        const cleanupPromise = entry.cleanupPromise;
        if (cleanupPromise === null) return this.queueLoad(entry);

        entry.reloadPromise = cleanupPromise.then(() => {
            entry.cleanupPromise = null;
            entry.reloadPromise = null;

            if (entry.owners.size === 0) {
                if (this.entries.get(imageUrl) === entry)
                    this.entries.delete(imageUrl);
                return new Promise<Texture>(() => undefined);
            }

            return this.queueLoad(entry);
        });
        return entry.reloadPromise;
    }

    private drainQueue(): void {
        while (
            this.activeLoadCount < MAX_CONCURRENT_IMAGE_ASSET_LOADS &&
            this.queuedEntries.length > 0
        ) {
            const entry = this.queuedEntries.shift();
            if (!entry) return;

            if (
                this.entries.get(entry.imageUrl) !== entry ||
                entry.state !== "queued" ||
                entry.owners.size === 0
            ) {
                continue;
            }

            entry.state = "loading";
            this.activeLoadCount += 1;
            void this.load(entry.imageUrl, entry).finally(() => {
                this.activeLoadCount -= 1;
                this.drainQueue();
            });
        }
    }

    private async load(
        imageUrl: string,
        entry: ImageAssetEntry,
    ): Promise<void> {
        try {
            const texture = await Assets.load<Texture>(imageUrl);
            entry.texture = texture;
            entry.state = "loaded";
            entry.resolveLoad?.(texture);

            if (entry.owners.size === 0) this.unload(imageUrl, entry);
        } catch (error) {
            // A failed acquisition owns nothing. Removing the entry lets a later,
            // independent acquisition make one fresh Pixi load attempt.
            entry.owners.clear();
            if (this.entries.get(imageUrl) === entry)
                this.entries.delete(imageUrl);
            entry.rejectLoad?.(error);
        }
    }

    private release(
        imageUrl: string,
        entry: ImageAssetEntry,
        owner: symbol,
    ): void {
        entry.owners.delete(owner);
        if (entry.owners.size !== 0) return;

        if (entry.state === "queued") {
            // Releasing abandons the returned texturePromise. A queued load skipped
            // here deliberately leaves that abandoned promise pending rather than
            // introducing a cancellation error solely to settle it.
            if (this.entries.get(imageUrl) === entry)
                this.entries.delete(imageUrl);
            return;
        }

        if (entry.state === "loaded") this.unload(imageUrl, entry);
    }

    private unload(imageUrl: string, entry: ImageAssetEntry): void {
        if (entry.cleanupPromise !== null) return;

        entry.state = "unloading";
        entry.loadPromise = null;
        entry.resolveLoad = null;
        entry.rejectLoad = null;
        entry.cleanupPromise = Assets.unload(imageUrl)
            .catch((error: unknown) => {
                // Pixi cleanup is best-effort. The subsequent handler still clears
                // local ownership and texture state so this entry cannot get stuck.
                console.error(
                    `Image overlay unload failed for ${imageUrl}`,
                    error,
                );
            })
            .then(() => {
                entry.texture = null;
                if (
                    entry.owners.size === 0 &&
                    this.entries.get(imageUrl) === entry
                ) {
                    this.entries.delete(imageUrl);
                }
            });
    }
}

const imageAssetManager = new ImageAssetManager();

export function acquireOwnedImageAsset(imageUrl: string): OwnedImageAsset {
    return imageAssetManager.acquire(imageUrl);
}
